import { RefObject, useCallback, useRef } from 'react';
import { AFrameCamera, AFrameScene, FOVManagementHook } from '../types';

export const FOV = 80;
export const MIN_FOV = 10;
export const MAX_FOV = 80;

export const useFOVManagement = (
  cameraRef: RefObject<AFrameCamera | null>,
  secondCameraRef: RefObject<AFrameCamera | null>,
  isSplitView: boolean,
): FOVManagementHook => {
  const fovRef = useRef(FOV);

  const setFov = useCallback((nextFov: number, pointerX?: number, pointerY?: number): void => {
    const cameraEl = cameraRef.current;
    if (!cameraEl) return;

    const clamped = Math.max(MIN_FOV, Math.min(MAX_FOV, Number(nextFov)));
    const oldFov = fovRef.current;
    fovRef.current = clamped;
    cameraEl.setAttribute('camera', 'fov', clamped.toString());

    // Also update second camera FOV in split view
    if (isSplitView && secondCameraRef.current) {
      secondCameraRef.current.setAttribute('camera', 'fov', clamped.toString());
    }

    // Pointer zoom: adjust camera rotation to zoom towards pointer position
    if (pointerX !== undefined && pointerY !== undefined) {
      const sceneEl = cameraEl.parentElement as AFrameScene;
      if (sceneEl) {
        const canvas = sceneEl.canvas ?? sceneEl;
        const rect = canvas.getBoundingClientRect();

        // Convert screen coordinates to normalized device coordinates [-1, 1]
        const x = ((pointerX - rect.left) / rect.width) * 2 - 1;
        const y = -(((pointerY - rect.top) / rect.height) * 2 - 1);

        // Skip if near center (no adjustment needed)
        if (Math.abs(x) > 0.1 || Math.abs(y) > 0.1) {
          const controls = cameraEl.components?.['custom-look-controls'];
          if (controls) {
            // Calculate how much the view has changed due to FOV change
            const aspect = rect.width / rect.height;
            const oldFovRad = oldFov * Math.PI / 180;
            const newFovRad = clamped * Math.PI / 180;

            // Calculate angular offsets based on FOV difference
            const fovDiff = oldFovRad - newFovRad;
            const yawOffset = -x * (fovDiff * aspect) * 0.5; // Invert X for correct left/right
            const pitchOffset = y * fovDiff * 0.5;

            // Apply rotation adjustment
            const currentYaw = controls.yawObject.rotation.y;
            const currentPitch = controls.pitchObject.rotation.x;

            controls.yawObject.rotation.y = currentYaw + yawOffset;
            controls.pitchObject.rotation.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, currentPitch + pitchOffset));
          }
        }
      }
    }

    const sceneEl = cameraEl.parentElement as AFrameScene;
    if (sceneEl?.resize) {
      sceneEl.resize();
    }

    if (isSplitView && secondCameraRef.current) {
      const secondSceneEl = secondCameraRef.current.parentElement as AFrameScene;
      if (secondSceneEl?.resize) {
        secondSceneEl.resize();
      }
    }
  }, [isSplitView, cameraRef, secondCameraRef]);

  const setSecondFov = useCallback((
    nextFov: number,
    pointerX?: number,
    pointerY?: number,
    secondSceneRef?: RefObject<AFrameScene | null>
  ): void => {
    const cameraEl = secondCameraRef.current;
    if (!cameraEl) return;

    const clamped = Math.max(MIN_FOV, Math.min(MAX_FOV, Number(nextFov)));
    const oldFov = fovRef.current;
    fovRef.current = clamped;
    cameraEl.setAttribute('camera', 'fov', clamped.toString());

    // Also update main camera FOV in split view
    if (isSplitView && cameraRef.current) {
      cameraRef.current.setAttribute('camera', 'fov', clamped.toString());
    }

    // Pointer zoom: adjust camera rotation to zoom towards pointer position
    if (pointerX !== undefined && pointerY !== undefined && secondSceneRef) {
      const sceneEl = secondSceneRef.current;
      if (sceneEl) {
        const canvas = sceneEl.canvas ?? sceneEl;
        const rect = canvas.getBoundingClientRect();

        // Convert screen coordinates to normalized device coordinates [-1, 1]
        const x = ((pointerX - rect.left) / rect.width) * 2 - 1;
        const y = -(((pointerY - rect.top) / rect.height) * 2 - 1);

        // Skip if near center (no adjustment needed)
        if (Math.abs(x) > 0.1 || Math.abs(y) > 0.1) {
          const controls = cameraEl.components?.['custom-look-controls'];
          if (controls) {
            // Calculate how much the view has changed due to FOV change
            const aspect = rect.width / rect.height;
            const oldFovRad = oldFov * Math.PI / 180;
            const newFovRad = clamped * Math.PI / 180;

            // Calculate angular offsets based on FOV difference
            const fovDiff = oldFovRad - newFovRad;
            const yawOffset = -x * (fovDiff * aspect) * 0.5; // Invert X for correct left/right
            const pitchOffset = y * fovDiff * 0.5;

            // Apply rotation adjustment
            const currentYaw = controls.yawObject.rotation.y;
            const currentPitch = controls.pitchObject.rotation.x;

            controls.yawObject.rotation.y = currentYaw + yawOffset;
            controls.pitchObject.rotation.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, currentPitch + pitchOffset));
          }
        }
      }
    }

    if (cameraRef.current) {
      const mainSceneEl = cameraRef.current.parentElement as AFrameScene;

      if (mainSceneEl?.resize) {
        mainSceneEl.resize();
      }
    }
    if (secondSceneRef?.current?.resize) {
      secondSceneRef.current.resize();
    }
  }, [isSplitView, cameraRef, secondCameraRef]);

  return {
    fovRef,
    setFov,
    setSecondFov,
  };
};
